import React, { forwardRef } from 'react';
import styled from 'styled-components';
import colors from '@/common/view/colors';
import typescales, { Typescales } from '@/common/view/typescales';

export type InputSize = 'xsmall' | 'small' | 'medium';

const sizeMap: Record<InputSize, { padding: string; height: string; typescale: Typescales }> = {
  xsmall: {
    padding: '6px 12px',
    height: '30px',
    typescale: 'caption1',
  },
  small: {
    padding: '10px 12px',
    height: '40px',
    typescale: 'body4',
  },
  medium: {
    padding: '10px 12px',
    height: '44px',
    typescale: 'body1',
  },
} as const;

const StyledInput = styled.input<{ $error?: boolean; $size: InputSize; $width?: string | number }>`
  ${({ $width }) => ($width ? `width: ${$width};` : '')};
  ${({ $size }) => {
    const { padding, height, typescale } = sizeMap[$size];
    return `
      padding: ${padding};
      height: ${height};
      ${typescales[typescale]}
    `;
  }}

  border-radius: 8px;
  border: 1px solid ${({ $error }) => ($error ? colors.red50 : colors.gray30)};
  background: ${colors.grayWhite};

  &::placeholder {
    color: ${colors.gray40};
    opacity: 1;
  }
  &::-ms-input-placeholder {
    color: ${colors.gray40};
  }
  &:hover {
    border-color: ${({ $error }) => ($error ? colors.red50 : colors.grayBlack)};
  }
  &:focus,
  &:focus-visible {
    outline: ${({ $error }) => ($error ? colors.red50 : colors.gray100)} auto 1px;
  }
  &:disabled {
    cursor: not-allowed;
    border-color: ${colors.gray30};
    background: ${colors.gray10};
  }
`;

type HTMLInputTypes = React.DetailedHTMLProps<
  React.InputHTMLAttributes<HTMLInputElement>,
  HTMLInputElement
>;
export interface InputProps extends Omit<HTMLInputTypes, 'size'> {
  error?: boolean;
  size?: InputSize;
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ error, size = 'medium', width, ...restProps }, ref) => {
    return <StyledInput $error={error} $size={size} $width={width} ref={ref} {...restProps} />;
  },
);

Input.displayName = 'Input';
export default Input;
